import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export function SearchableSelect({ value, onValueChange, options, placeholder = 'Select...', searchPlaceholder = 'Search...', emptyText = 'No results.', className, triggerClassName, disabled = false, frequentValues, 'data-testid': testId }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);

  const frequentOptions = frequentValues?.length
    ? frequentValues.map(fv => options.find(o => o.value === fv.value || o.label === fv.value)).filter(Boolean)
    : [];

  const handleSelect = (optionValue) => {
    onValueChange(optionValue === value ? '' : optionValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className={cn('w-full justify-between font-normal', !selected && 'text-muted-foreground', triggerClassName)} data-testid={testId}>
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', className)} align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-8 text-sm" />
          <CommandList>
            <CommandEmpty className="py-3 text-xs">{emptyText}</CommandEmpty>
            {frequentOptions.length > 0 && (
              <>
                <CommandGroup heading={<span className="flex items-center gap-1 text-[10px]"><Sparkles className="h-3 w-3 text-amber-500" />Frequently Used</span>}>
                  {frequentOptions.map(o => (
                    <CommandItem key={`freq-${o.value}`} value={o.label} onSelect={() => handleSelect(o.value)} className="text-sm">
                      <Check className={cn('mr-1.5 h-3.5 w-3.5', value === o.value ? 'opacity-100' : 'opacity-0')} />
                      {o.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading={frequentOptions.length > 0 ? 'All Options' : undefined}>
              {options.map(o => (
                <CommandItem key={o.value} value={o.label} onSelect={() => handleSelect(o.value)} className="text-sm">
                  <Check className={cn('mr-1.5 h-3.5 w-3.5', value === o.value ? 'opacity-100' : 'opacity-0')} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
